import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type BaseUser } from 'Users';
import { type ReportsStream } from './Reports.types';

// The calls of the User Detail tab. Unlike reportsApi, these convert casing, so the user and the
// streams arrive in camelCase for the Users and ProgramInclusion helpers.
const baseConfig = getBackRoyalApiBaseConfig('reportsUserDetailApi');

// The fields the tab reads. The server returns every field when the request names none.
const STREAM_FIELDS = [
    'id',
    'title',
    'lessons',
    'chapters',
    'image',
    'locale_pack',
    'lesson_streams_progress',
    'time_limit_hours',
    'exam',
    'program_types',
    'coming_soon',
];
const LESSON_FIELDS = ['id', 'title', 'assessment', 'locale_pack', 'lesson_progress', 'test'];

// The shared base query retries a dropped connection. A retried DELETE or PUT is safe here: both
// reset to the same state.
export const userDetailApi = createApi('reportsUserDetailApi', {
    ...baseConfig,
    tagTypes: ['Streams'],
    endpoints: builder => ({
        getReportsUser: builder.query<BaseUser | undefined, string>({
            query: userId => ({ url: `users/${userId}.json` }),
            transformResponse: (response: { contents: { users: BaseUser[] } }) => response.contents.users[0],
        }),

        getUserStreams: builder.query<ReportsStream[], { userId: string; cohortIds: string[] }>({
            query: ({ userId, cohortIds }) => ({
                method: 'POST',
                url: 'lesson_streams/index.json',
                body: {
                    includeProgress: true,
                    userId,
                    filters: { cohortIds },
                    fields: STREAM_FIELDS,
                    lessonFields: LESSON_FIELDS,
                },
            }),
            transformResponse: (response: { contents: { lessonStreams: ReportsStream[] } }) =>
                response.contents.lessonStreams ?? [],
            providesTags: ['Streams'],
        }),

        resetStreamProgress: builder.mutation<void, string>({
            query: streamProgressId => ({ method: 'DELETE', url: `lesson_streams_progress/${streamProgressId}.json` }),
            invalidatesTags: ['Streams'],
        }),

        // The controller only clears time_runs_out_at (stream_progress_controller.rb#update).
        resetExamTimer: builder.mutation<void, string>({
            query: streamProgressId => ({
                method: 'PUT',
                url: 'lesson_streams_progress.json',
                body: { record: { id: streamProgressId, timeRunsOutAt: null } },
            }),
            invalidatesTags: ['Streams'],
        }),
    }),
});

export const {
    useGetReportsUserQuery,
    useGetUserStreamsQuery,
    useResetStreamProgressMutation,
    useResetExamTimerMutation,
} = userDetailApi;
